import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ProductCard from '../ProductCard';
import { Product, CustomOptionData, CustomOption } from 'types/Product';
import { filterStateSelector } from 'store/ui/selectors';
import NoElements from './NoElements';
import { TrackingProp } from 'react-tracking';
import { GridItem, GridWrapper, LimitedResultsWrapper } from './styled';
import { useSplitTreatment } from '../../hooks/splits/useSplitTreatment';
import { YotpoItems, CLPFilter } from '../../types/Category';
import { cardCategoryIds } from 'constants/clp';

export type ProductGridProduct = Product & {
  canonicalUrl?: string;
  clpHoverImage: string;
  clpLink?: string;
};

interface ProductGridProps {
  products: ProductGridProduct[];
  fourColumnGrid?: boolean;
  isBookCLP?: boolean;
  isPhotoBookCLP: boolean;
  shouldShowPrices?: boolean;
  totalPages: number;
  tracking?: TrackingProp;
  yotpoData: YotpoItems[];
  categoryId: number;
}

const SPLIT_IO_FEATURE_PRODUCT_CALLOUTS = 'FeatureFlag-EN8502-Product_Callouts-_AB_test_Photo_Book_CLP-Desktop';
const SPLIT_IO_FEATURE_PRODUCT_CALLOUTS_COOKIE = 'au_split_feature_flag_product_callouts';

const ProductGrid = ({
  products = [],
  fourColumnGrid = false,
  isBookCLP = false,
  isPhotoBookCLP,
  shouldShowPrices,
  totalPages,
  tracking,
  yotpoData,
  categoryId
}: ProductGridProps): React.JSX.Element => {
  const filtersApplied = useSelector(filterStateSelector);
  const [filterArray, setFilters] = useState([]);

  const { treatmentStatus: isProductCalloutSplit } = useSplitTreatment(
    SPLIT_IO_FEATURE_PRODUCT_CALLOUTS,
    SPLIT_IO_FEATURE_PRODUCT_CALLOUTS_COOKIE
  );

  useEffect(() => {
    const filters = filtersApplied.filters;
    const scrubFilters = filters.filter(
      filter =>
        filter.attributeCode === 'filter_printing_style' ||
        filter.attributeCode === 'filter_card_orientation' ||
        filter.attributeCode === 'filter_holiday_multiselect'
    );
    setFilters(scrubFilters);
  }, [filtersApplied]);

  // This one needs to be handled separetly since is the only one with content in filterValue for product working as a sub category for our purpose
  const parsedGreetingFilter = (product: Product, filter: CLPFilter) => {
    const lastFilterOptionSelected = filter.options[filter.options.length - 1].toLowerCase();
    const foundCustomOptionInProduct = product.customOptions.find(
      (customOption: CustomOption) => customOption.filterAttribute === filter.attributeCode
    );
    if (foundCustomOptionInProduct) {
      const foundOption = foundCustomOptionInProduct.values.find(
        (valueOption: CustomOptionData) => valueOption.filterValue.toLowerCase() === lastFilterOptionSelected
      );
      return foundOption ? foundOption.label : '';
    } else {
      return '';
    }
  };

  const buildCanonicalUrl = (product: Product) => {
    if (filterArray.length === 0) {
      return product.canonicalUrl;
    }

    const filterParams = filterArray.map((filter: CLPFilter) => {
      if (filter.attributeCode === 'filter_holiday_multiselect') {
        return parsedGreetingFilter(product, filter);
      } else {
        return filter.options[filter.options.length - 1].toLowerCase();
      }
    });

    if (!filterParams || filterParams.length === 0) {
      return product.canonicalUrl;
    }

    const cleanFilterParams = filterParams.filter((filterParam: string) => filterParam !== '');
    return `${product.canonicalUrl}/${cleanFilterParams.join('/')}`.replace('-stamped', '');
  };

  const getPrioritizedUrl = (givenProduct: Product) => givenProduct.clpLink || buildCanonicalUrl(givenProduct);

  const showLimitedResultBlock = fourColumnGrid && totalPages <= 1 && cardCategoryIds.includes(categoryId);

  return (
    <div id="product-grid">
      <GridWrapper fourColumnGrid={fourColumnGrid} isTwoUpMobile={isBookCLP}>
        {products.map((product, index) => (
          <GridItem key={index}>
            <ProductCard
              product={product}
              canonicalUrl={getPrioritizedUrl(product)}
              shouldShowPrices={shouldShowPrices}
              isPhotoBookCLP={isPhotoBookCLP}
              isProductCalloutSplit={isProductCalloutSplit}
              index={index}
              yotpoData={yotpoData}
            />
          </GridItem>
        ))}
        {showLimitedResultBlock && (
          <LimitedResultsWrapper>
            <NoElements tracking={tracking} />
          </LimitedResultsWrapper>
        )}
      </GridWrapper>
    </div>
  );
};

export default ProductGrid;
